/**
 * Self-Service Portal — Staff personal dashboard
 * Features: Clock In/Out (GPS), Profile, Payslip, Attendance, Leave, KPI, Training, Expense, EA Form
 * Mobile-first, optimized for staff to use on phone.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  MapPin, Clock, User as UserIcon, FileText, Calendar, Award, BookOpen, Receipt,
  CheckCircle, AlertCircle, LogOut, ChevronRight, Loader, Plus, Building2, X,
  Download, Smartphone, Camera, Sparkles, Coins, Home, Wallet, UserCircle,
  Flame, PartyPopper, Cake, ArrowRight, ShieldCheck, LogIn, ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import {
  Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader,
  DrawerTitle,
} from '../components/ui/drawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny haptic helper — fires only if browser/device supports it
const haptic = (pattern = 8) => {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch { /* noop */ }
};

// Main 4-tab groups (design guidelines)
const TAB_GROUPS = [
  { id: 'harian', label: 'Harian', icon: Home, sections: ['home', 'attendance'] },
  { id: 'gaji', label: 'Gaji', icon: Wallet, sections: ['payslip', 'overtime', 'advance', 'ea'] },
  { id: 'permohonan', label: 'Permohonan', icon: FileText, sections: ['leave', 'expense'] },
  { id: 'profil', label: 'Profil', icon: UserCircle, sections: ['profile', 'kpi'] },
];
// Sub-tabs inside each group (label + icon + section key)
const SUB_TABS = {
  harian: [
    { id: 'home', label: 'Utama', icon: Clock },
    { id: 'attendance', label: 'Kehadiran', icon: Calendar },
  ],
  gaji: [
    { id: 'payslip', label: 'Slip Gaji', icon: FileText },
    { id: 'overtime', label: 'OT Claim', icon: Clock },
    { id: 'advance', label: 'Advance Gaji', icon: Coins },
    { id: 'ea', label: 'EA Form', icon: FileText },
  ],
  permohonan: [
    { id: 'leave', label: 'Cuti', icon: BookOpen },
    { id: 'expense', label: 'Tuntutan', icon: Receipt },
  ],
  profil: [
    { id: 'profile', label: 'Maklumat', icon: UserIcon },
    { id: 'kpi', label: 'KPI', icon: Award },
  ],
};

const greetingFor = (hour) => {
  if (hour < 12) return 'Selamat Pagi';
  if (hour < 15) return 'Selamat Tengah Hari';
  if (hour < 19) return 'Selamat Petang';
  return 'Selamat Malam';
};

const firstName = (full) => (full || 'Kawan').split(/\s+/)[0];

const SelfServicePortalPage = () => {
  const { user, userRole } = useApp();
  const [profile, setProfile] = useState(null);
  const [clockStatus, setClockStatus] = useState(null);
  const [officeConfig, setOfficeConfig] = useState(null);
  const [extra, setExtra] = useState({ leaves: [], otClaims: [], expClaims: [], payslip: null, attendance: [] });
  const [activeGroup, setActiveGroup] = useState('harian');
  const [activeSection, setActiveSection] = useState('home');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHome = useCallback(async () => {
    try {
      const [pRes, cRes, oRes] = await Promise.all([
        axios.get(`${API}/me/profile`),
        axios.get(`${API}/me/clock-status`),
        axios.get(`${API}/me/office-config`),
      ]);
      setProfile(pRes.data);
      setClockStatus(cRes.data);
      setOfficeConfig(oRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        // Expected: user is not linked to a staff record (e.g., Director/Finance/HR landing here)
        toast.error('Akaun anda belum dipautkan ke rekod staf. Sila hubungi HR.');
      } else {
        console.error('Failed to load self-service data:', e);
        toast.error('Gagal memuat data portal');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExtras = useCallback(async () => {
    // Best-effort secondary fetches for dashboard widgets
    const month = new Date().toISOString().slice(0, 7);
    const [lv, ot, ex, ps, at] = await Promise.allSettled([
      axios.get(`${API}/me/leave`),
      axios.get(`${API}/me/overtime-claims`),
      axios.get(`${API}/me/expense-claims`),
      axios.get(`${API}/me/payslip?month=${month}`).catch(() => null),
      axios.get(`${API}/me/attendance?month=${month}`),
    ]);
    setExtra({
      leaves: lv.status === 'fulfilled' ? (lv.value.data.leaves || lv.value.data.items || []) : [],
      otClaims: ot.status === 'fulfilled' ? (ot.value.data.claims || ot.value.data.items || []) : [],
      expClaims: ex.status === 'fulfilled' ? (ex.value.data.claims || ex.value.data.items || []) : [],
      payslip: ps.status === 'fulfilled' ? ps.value?.data : null,
      attendance: at.status === 'fulfilled' ? (at.value.data.pdf_records || at.value.data.records || []) : [],
    });
  }, []);

  // Combined refresh used by pull-to-refresh + manual button
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    haptic(15);
    try { await Promise.all([fetchHome(), fetchExtras()]); }
    finally { setRefreshing(false); }
  }, [fetchHome, fetchExtras]);

  useEffect(() => { fetchHome(); }, [fetchHome]);
  useEffect(() => { if (profile) fetchExtras(); }, [profile, fetchExtras]);

  // Sync active group when section changes (e.g., deep linking later)
  useEffect(() => {
    const found = TAB_GROUPS.find((g) => g.sections.includes(activeSection));
    if (found) setActiveGroup(found.id);
  }, [activeSection]);

  // Detect PWA standalone mode for chrome adjustments
  const isStandalone = useMemo(
    () => typeof window !== 'undefined' && (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true
    ),
    []
  );

  if (loading) {
    return (
      <div className="portal-scope min-h-[100dvh] p-4 sm:p-6 pb-28 md:pb-10" data-testid="portal-loading">
        <div className="max-w-5xl mx-auto space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full portal-skeleton" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 portal-skeleton" />
              <div className="h-5 w-40 portal-skeleton" />
            </div>
          </div>
          <div className="portal-skeleton h-48 rounded-2xl" />
          <div className="grid grid-cols-3 gap-3">
            <div className="portal-skeleton h-28 rounded-2xl" />
            <div className="portal-skeleton h-28 rounded-2xl" />
            <div className="portal-skeleton h-28 rounded-2xl" />
          </div>
          <div className="portal-skeleton h-32 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!profile) {
    const isAdminUser = ['director', 'finance', 'hr', 'viewer'].includes(userRole);
    return (
      <div className="portal-scope min-h-[100dvh] p-6 flex items-center justify-center">
        <div className="max-w-md w-full portal-card p-8 text-center" data-testid="portal-no-account">
          <AlertCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--portal-warning)' }} />
          <h2 className="portal-heading text-xl font-bold mb-2">Akaun Tidak Ditemui</h2>
          <p className="text-sm" style={{ color: 'var(--portal-text-secondary)' }}>
            Email <strong style={{ color: 'var(--portal-text)' }}>{user?.email}</strong> tidak dipautkan ke rekod staf.
            {isAdminUser ? ' Halaman ini khas untuk staf yang ada rekod.' : ' Sila hubungi HR untuk akses.'}
          </p>
          {isAdminUser && (
            <Link to="/" data-testid="back-to-dashboard"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold portal-btn"
              style={{ background: 'var(--portal-maroon)', color: '#FFF' }}>
              <ArrowLeft className="w-4 h-4" /> Kembali ke Dashboard
            </Link>
          )}
        </div>
      </div>
    );
  }

  const now = new Date();
  const greeting = greetingFor(now.getHours());
  const name = firstName(profile.name);
  const streak = computeStreak(extra.attendance);
  const celebration = detectCelebration(profile);
  const isAdminUser = ['director', 'finance', 'hr', 'viewer'].includes(userRole);

  return (
    <div className="portal-scope min-h-[100dvh] p-4 sm:p-6 lg:p-8 pb-28 md:pb-10" data-testid="portal-root">
      {/* Pull-to-refresh wrapper */}
      <PullToRefresh onRefresh={refreshAll} refreshing={refreshing} />

      {/* Sticky greeting header — acts as PWA "title bar" */}
      <div data-testid="portal-sticky-greeting" className="sticky -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-1 pb-3 z-20 mb-4 sm:mb-6"
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          background: 'linear-gradient(180deg, var(--portal-bg) 0%, var(--portal-bg) 70%, transparent 100%)',
        }}>
        <div className="flex items-center gap-3">
          <div data-testid="portal-avatar" className="w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
            style={{ background: 'var(--portal-maroon)', color: '#FFF', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {profile.name?.[0] || 'S'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>
              {greeting}
            </p>
            <h1 data-testid="portal-greeting-name" className="portal-heading text-xl md:text-2xl font-bold truncate leading-tight" style={{ color: 'var(--portal-text)' }}>
              {name}
              {streak >= 3 && (
                <span data-testid="streak-badge" className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold align-middle"
                  style={{ background: 'var(--portal-warning-bg)', color: '#B25A00' }}>
                  <Flame className="w-3 h-3" /> {streak} hari
                </span>
              )}
            </h1>
          </div>
          {/* Refresh icon (mobile) — visible top-right when no admin */}
          {!isAdminUser && (
            <button
              data-testid="portal-refresh-btn"
              onClick={refreshAll}
              aria-label="Refresh"
              className="md:hidden w-9 h-9 rounded-full flex items-center justify-center portal-btn"
              style={{ background: 'rgba(127,29,29,0.06)' }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: 'var(--portal-maroon)' }} />
            </button>
          )}
          {/* Admin: back to dashboard pill */}
          {isAdminUser && (
            <Link to="/" data-testid="portal-back-to-dashboard"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold portal-btn"
              style={{ background: 'rgba(127,29,29,0.06)', color: 'var(--portal-maroon)' }}>
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </Link>
          )}
          <div className="text-right hidden sm:block">
            <p className="text-[11px] tracking-wider uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>
              {now.toLocaleDateString('ms-MY', { weekday: 'long' })}
            </p>
            <p className="text-sm font-semibold" style={{ color: 'var(--portal-text)' }}>
              {now.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>

      {/* Main content flows here — full width on mobile, max-w constrained on desktop */}
      <div key={activeSection} className="max-w-5xl mx-auto portal-stagger portal-section-enter space-y-4 sm:space-y-5">
        {celebration && <CelebrationBanner celebration={celebration} />}

        {activeSection === 'home' && (
          <HomeSection profile={profile} clockStatus={clockStatus} officeConfig={officeConfig}
            extra={extra} onRefresh={refreshAll} onJump={(s) => { haptic(5); setActiveSection(s); }} />
        )}
        {activeSection === 'profile' && <ProfileSection profile={profile} onSave={fetchHome} />}
        {activeSection === 'payslip' && <PayslipSection />}
        {activeSection === 'attendance' && <AttendanceSection />}
        {activeSection === 'leave' && <LeaveSection />}
        {activeSection === 'kpi' && <KPISection />}
        {activeSection === 'expense' && <ExpenseSection />}
        {activeSection === 'overtime' && <OvertimeSection />}
        {activeSection === 'advance' && <AdvanceSection />}
        {activeSection === 'ea' && <EAFormSection />}

        {/* Sub-tabs within active group (shown on Harian to switch to Attendance, etc.) */}
        {SUB_TABS[activeGroup].length > 1 && (
          <div data-testid="portal-subtabs" className="flex gap-2 overflow-x-auto no-scrollbar py-1">
            {SUB_TABS[activeGroup].map((s) => (
              <button
                key={s.id}
                data-testid={`subtab-${s.id}`}
                onClick={() => { haptic(5); setActiveSection(s.id); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap portal-btn flex-shrink-0 border ${
                  activeSection === s.id ? 'text-white' : ''
                }`}
                style={
                  activeSection === s.id
                    ? { background: 'var(--portal-maroon)', borderColor: 'var(--portal-maroon)' }
                    : { background: 'var(--portal-surface)', borderColor: 'var(--portal-border)', color: 'var(--portal-text)' }
                }
              >
                <s.icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            ))}
          </div>
        )}

        {/* PWA install — appears ONCE after main content, dismissible */}
        {!isStandalone && <InstallAppCard />}
      </div>

      {/* Bottom nav (mobile) */}
      <nav data-testid="portal-bottom-nav" className="portal-bottom-nav md:hidden">
        <div className="flex items-stretch justify-around">
          {TAB_GROUPS.map((g) => {
            const isActive = g.id === activeGroup;
            const Icon = g.icon;
            return (
              <button
                key={g.id}
                data-testid={`nav-${g.id}`}
                onClick={() => {
                  haptic(8);
                  setActiveGroup(g.id);
                  setActiveSection(g.sections[0]);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex-1 flex flex-col items-center gap-0.5 py-2.5 portal-btn"
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.4 : 1.8}
                  style={{ color: isActive ? 'var(--portal-maroon)' : 'var(--portal-text-secondary)' }} />
                <span className="text-[10px] font-semibold tracking-wide"
                  style={{ color: isActive ? 'var(--portal-maroon)' : 'var(--portal-text-secondary)' }}>
                  {g.label}
                </span>
                {isActive && <span className="w-1 h-1 rounded-full" style={{ background: 'var(--portal-maroon)' }} />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop top pill nav */}
      <div data-testid="portal-desktop-nav" className="hidden md:flex fixed top-6 right-6 xl:right-10 z-30 gap-1 p-1 rounded-full shadow-lg"
        style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', border: '1px solid var(--portal-border)' }}>
        {TAB_GROUPS.map((g) => {
          const isActive = g.id === activeGroup;
          const Icon = g.icon;
          return (
            <button
              key={g.id}
              data-testid={`desknav-${g.id}`}
              onClick={() => {
                haptic(8);
                setActiveGroup(g.id);
                setActiveSection(g.sections[0]);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold portal-btn ${
                isActive ? 'text-white' : ''
              }`}
              style={isActive ? { background: 'var(--portal-maroon)' } : { color: 'var(--portal-text-secondary)' }}
            >
              <Icon className="w-4 h-4" /> {g.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// PULL-TO-REFRESH — touch gesture for mobile native feel
// ─────────────────────────────────────────────────────────────────────────
const PullToRefresh = ({ onRefresh, refreshing }) => {
  const [pull, setPull] = useState(0);
  const startY = useRef(0);
  const pulling = useRef(false);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    };
    const onTouchMove = (e) => {
      if (!pulling.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0 && window.scrollY === 0) {
        // dampen the pull
        setPull(Math.min(delta * 0.5, 80));
      }
    };
    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pull > 60) {
        haptic([10, 30, 10]);
        onRefresh();
      }
      setPull(0);
    };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [pull, onRefresh]);

  if (!pull && !refreshing) return null;
  const ready = pull > 60;
  return (
    <div data-testid="portal-ptr-indicator" className="portal-ptr-indicator"
      style={{ '--ptr-y': `${refreshing ? 60 : pull - 40}px`, opacity: refreshing ? 1 : Math.min(pull / 60, 1) }}>
      <RefreshCw
        className={refreshing ? 'animate-spin' : ''}
        style={{
          color: ready || refreshing ? 'var(--portal-maroon)' : 'var(--portal-text-secondary)',
          width: 18, height: 18,
          transform: refreshing ? 'none' : `rotate(${pull * 4}deg)`,
          transition: 'transform 0.1s linear',
        }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// SELFIE CAPTURE — opt-in, privacy-aware. Uses getUserMedia (front camera).
// Fallback: <input type="file" capture> when camera API unavailable.
// ─────────────────────────────────────────────────────────────────────────
const SelfieCapture = ({ selfieB64, onCapture, onClear }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    setError(''); setRequesting(true);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) {
      setError('Kamera tidak dapat diakses. Benarkan akses kamera atau pilih fail imej.');
    } finally {
      setRequesting(false);
    }
  };

  const stopCamera = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  useEffect(() => () => stopCamera(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current; const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight) || 320;
    c.width = 320; c.height = 320;
    const ctx = c.getContext('2d');
    const sx = (v.videoWidth - size) / 2; const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, 320, 320);
    const b64 = c.toDataURL('image/jpeg', 0.75);
    onCapture(b64);
    stopCamera();
    haptic(12);
  };

  const handleFileFallback = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setError('Saiz imej maksimum 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div data-testid="selfie-capture" className="portal-card p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] tracking-wider uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>
          Selfie <span className="font-normal normal-case tracking-normal opacity-75">(Pilihan · anti-buddy-punch)</span>
        </p>
        {selfieB64 && (
          <button type="button" onClick={onClear}
            className="text-[11px] font-semibold portal-btn px-2 py-0.5 rounded-full"
            style={{ color: 'var(--portal-danger)', background: 'var(--portal-danger-bg)' }}>
            Buang
          </button>
        )}
      </div>

      {selfieB64 ? (
        <div className="flex items-center gap-3">
          <img src={selfieB64} alt="selfie" className="w-16 h-16 rounded-xl object-cover"
            style={{ border: '1px solid var(--portal-border)' }} />
          <div className="text-xs" style={{ color: 'var(--portal-success)' }}>
            <CheckCircle className="inline w-3.5 h-3.5 mr-1" />
            Selfie diambil — akan dilampirkan pada rekod clock-in.
          </div>
        </div>
      ) : stream ? (
        <div className="space-y-2">
          <video ref={videoRef} playsInline muted className="w-full rounded-xl" style={{ maxHeight: 240, background: '#000' }} />
          <canvas ref={canvasRef} className="hidden" />
          <div className="flex gap-2">
            <button type="button" onClick={snap} data-testid="selfie-snap-btn"
              className="flex-1 py-2 rounded-full text-sm font-semibold portal-btn"
              style={{ background: 'var(--portal-maroon)', color: '#FFF' }}>
              <Camera className="inline w-4 h-4 mr-1.5" /> Ambil
            </button>
            <button type="button" onClick={stopCamera}
              className="px-4 py-2 rounded-full text-sm font-semibold portal-btn"
              style={{ background: 'var(--portal-surface-secondary)', color: 'var(--portal-text)' }}>
              Batal
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <button type="button" onClick={startCamera} disabled={requesting} data-testid="selfie-start-btn"
              className="flex-1 py-2 rounded-full text-sm font-semibold portal-btn inline-flex items-center justify-center gap-1.5"
              style={{ background: 'var(--portal-surface-secondary)', color: 'var(--portal-text)' }}>
              <Camera className="w-4 h-4" /> {requesting ? 'Mohon akses...' : 'Buka Kamera'}
            </button>
            <label className="flex-1 py-2 rounded-full text-sm font-semibold portal-btn inline-flex items-center justify-center gap-1.5 cursor-pointer"
              style={{ background: 'var(--portal-surface-secondary)', color: 'var(--portal-text)' }}>
              <Plus className="w-4 h-4" /> Pilih Fail
              <input type="file" accept="image/*" capture="user" className="hidden" onChange={handleFileFallback}
                data-testid="selfie-file-input" />
            </label>
          </div>
          {error && <p className="text-[11px] mt-2" style={{ color: 'var(--portal-danger)' }}>{error}</p>}
          <p className="text-[10px] italic mt-2" style={{ color: 'var(--portal-text-secondary)' }}>
            Foto disimpan hanya untuk audit HR; tidak dikongsi kepada pihak ketiga.
          </p>
        </>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────
// CELEBRATION BANNER — only shows on birthday / anniversary / big streak
// ─────────────────────────────────────────────────────────────────────────
const CelebrationBanner = ({ celebration }) => (
  <div data-testid="celebration-banner" className="celebration-gradient portal-card p-5 sm:p-6 flex items-center gap-4 overflow-hidden relative">
    <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: 'var(--portal-maroon)' }}>
      <celebration.icon className="w-7 h-7 text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] tracking-[0.15em] uppercase font-semibold" style={{ color: 'var(--portal-maroon)' }}>
        {celebration.label}
      </p>
      <h3 className="portal-heading text-lg sm:text-xl font-bold truncate" style={{ color: 'var(--portal-text)' }}>
        {celebration.message}
      </h3>
      {celebration.sub && (
        <p className="text-sm mt-0.5" style={{ color: 'var(--portal-text-secondary)' }}>
          {celebration.sub}
        </p>
      )}
    </div>
  </div>
);

function computeStreak(records) {
  // Count consecutive on-time days backwards from today
  if (!Array.isArray(records) || records.length === 0) return 0;
  const byDate = {};
  records.forEach((r) => {
    const d = r.date || (r.clock_in_time || '').slice(0, 10);
    if (!d) return;
    byDate[d] = r.status || (r.is_late ? 'late' : 'on_time');
  });
  let streak = 0;
  const d = new Date();
  for (let i = 0; i < 60; i++) {
    const key = d.toISOString().slice(0, 10);
    const st = byDate[key];
    if (!st) { d.setDate(d.getDate() - 1); continue; }
    if (st === 'late') break;
    streak += 1;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

function detectCelebration(profile) {
  const today = new Date();
  const mmdd = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  // Birthday — parse IC (YYMMDD prefix) if available
  if (profile.ic_number && profile.ic_number.length >= 6) {
    const ic = profile.ic_number.replace(/-/g, '');
    const mm = ic.slice(2, 4);
    const dd = ic.slice(4, 6);
    if (`${mm}-${dd}` === mmdd) {
      return {
        icon: Cake, label: 'Selamat Hari Lahir!',
        message: `Semoga hari istimewa anda penuh bermakna 🎂`,
        sub: 'Seluruh team UA Syarikat doakan yang terbaik untuk anda.',
      };
    }
  }
  // Work anniversary
  if (profile.join_date) {
    const jd = new Date(profile.join_date);
    if (!isNaN(jd.getTime())) {
      const jmm = String(jd.getMonth() + 1).padStart(2, '0');
      const jdd = String(jd.getDate()).padStart(2, '0');
      if (`${jmm}-${jdd}` === mmdd) {
        const years = today.getFullYear() - jd.getFullYear();
        if (years >= 1) {
          return {
            icon: PartyPopper, label: `${years} Tahun Bersama`,
            message: `Hari Ulang Tahun Kerja ke-${years}`,
            sub: 'Terima kasih atas komitmen anda kepada UA Syarikat.',
          };
        }
      }
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// HOME — Clock In/Out Hero + Pending Items Feed + Balance Cards
// ─────────────────────────────────────────────────────────────────────────
const HomeSection = ({ profile, clockStatus, officeConfig, extra, onRefresh, onJump }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState('in');
  const [locCheck, setLocCheck] = useState(null); // { distance, ok }
  const [justClockedIn, setJustClockedIn] = useState(false);
  const [selfieB64, setSelfieB64] = useState(null);

  const haversine = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const φ1 = (lat1 * Math.PI) / 180, φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180, Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Browser anda tidak menyokong GPS'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(new Error(err.message || 'Gagal dapatkan lokasi GPS')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  // Open confirmation drawer — open IMMEDIATELY, fetch location inside drawer
  const openConfirm = async (kind) => {
    setConfirmKind(kind);
    setError('');
    setLocCheck(null);
    setPosition(null);
    setConfirmOpen(true); // show drawer right away with loading state
    setBusy(true);
    try {
      const loc = await getLocation();
      setPosition(loc);
      const distance = officeConfig
        ? Math.round(haversine(loc.lat, loc.lng, officeConfig.lat, officeConfig.lng))
        : null;
      const ok = distance !== null && distance <= (officeConfig?.radius_meters || 150);
      setLocCheck({ distance, ok });
    } catch (e) {
      setError(e.message || 'Gagal dapatkan lokasi GPS. Pastikan kebenaran GPS diberikan.');
    } finally {
      setBusy(false);
    }
  };

  const confirmClock = async () => {
    if (!position) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/me/${confirmKind === 'in' ? 'clock-in' : 'clock-out'}`, {
        lat: position.lat, lng: position.lng, accuracy_meters: position.accuracy,
        selfie_b64: confirmKind === 'in' ? selfieB64 : undefined,
      });
      // Strong haptic on success — celebrates the clock action
      haptic([20, 60, 20]);
      toast.success(
        confirmKind === 'in'
          ? `Clock-in berjaya! ${data.status === 'late' ? '⏰ Anda lewat' : '✨ Tepat masa'}`
          : `Clock-out berjaya! Total: ${data.work_hours} jam`
      );
      setConfirmOpen(false);
      setSelfieB64(null);
      if (confirmKind === 'in') {
        setJustClockedIn(true);
        setTimeout(() => setJustClockedIn(false), 3000);
      }
      await onRefresh();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gagal proses clock';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const clockInTime = clockStatus?.record?.clock_in_time
    ? new Date(clockStatus.record.clock_in_time).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;
  const clockOutTime = clockStatus?.record?.clock_out_time
    ? new Date(clockStatus.record.clock_out_time).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;
  const isLate = clockStatus?.record?.status === 'late';

  // Pending approvals & balances
  const pendingLeaves = (extra.leaves || []).filter((l) => (l.status || '').toLowerCase() === 'pending');
  const pendingOT = (extra.otClaims || []).filter((l) => (l.status || '').toLowerCase() === 'pending');
  const pendingExp = (extra.expClaims || []).filter((l) => (l.status || '').toLowerCase() === 'pending');
  const pendingCount = pendingLeaves.length + pendingOT.length + pendingExp.length;

  const leaveBalance = profile.leave_balance ?? profile.annual_leave_balance ?? null;
  const netSalary = extra.payslip?.net_pay ?? extra.payslip?.net_salary ?? null;
  const hoursThisMonth = (extra.attendance || []).reduce((s, r) => s + (r.work_hours || 0), 0);

  return (
    <>
      {/* HERO — Clock In/Out card */}
      <div data-testid="clock-hero" className="portal-card hero-glow p-6 sm:p-8 relative overflow-hidden">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>
              Kehadiran Hari Ini
            </p>
            <h2 className="portal-heading text-2xl sm:text-3xl font-bold mt-1" style={{ color: 'var(--portal-text)' }}>
              {clockStatus?.clocked_in && clockStatus?.clocked_out
                ? 'Hari Selesai'
                : clockStatus?.clocked_in
                ? 'Sedang Bekerja'
                : 'Belum Masuk'}
            </h2>
          </div>
          <div className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'var(--portal-maroon-muted)' }}>
            <Clock className="w-5 h-5" style={{ color: 'var(--portal-maroon)' }} />
          </div>
        </div>

        {/* Status row — only shows filled states (progressive reveal, no empty em-dashes) */}
        {(clockInTime || clockOutTime) && (
          <div className="flex flex-wrap gap-2 mb-5">
            {clockInTime && (
              <div data-testid="clock-in-pill" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold"
                style={{
                  background: isLate ? 'var(--portal-warning-bg)' : 'var(--portal-success-bg)',
                  color: isLate ? '#B25A00' : 'var(--portal-success)',
                }}>
                <LogIn className="w-3.5 h-3.5" />
                Masuk {clockInTime}
                {isLate && <span className="opacity-75">• Lewat</span>}
              </div>
            )}
            {clockOutTime && (
              <div data-testid="clock-out-pill" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold"
                style={{ background: 'var(--portal-surface-secondary)', color: 'var(--portal-text)' }}>
                <LogOut className="w-3.5 h-3.5" />
                Keluar {clockOutTime}
                {clockStatus?.record?.work_hours && <span className="opacity-75">• {clockStatus.record.work_hours}j</span>}
              </div>
            )}
          </div>
        )}

        {/* Action button — progressive reveal */}
        {!clockStatus?.clocked_in && (
          <button
            data-testid="clock-in-btn"
            onClick={() => openConfirm('in')}
            disabled={busy}
            className={`w-full py-4 rounded-full font-bold text-base portal-btn flex items-center justify-center gap-2 ${justClockedIn ? 'animate-portal-pulse' : ''}`}
            style={{ background: 'var(--portal-maroon)', color: '#FFF' }}
          >
            {busy ? <Loader className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
            Clock-In Sekarang
          </button>
        )}
        {clockStatus?.clocked_in && !clockStatus?.clocked_out && (
          <button
            data-testid="clock-out-btn"
            onClick={() => openConfirm('out')}
            disabled={busy}
            className="w-full py-4 rounded-full font-bold text-base portal-btn flex items-center justify-center gap-2 border-2"
            style={{ borderColor: 'var(--portal-maroon)', color: 'var(--portal-maroon)', background: 'transparent' }}
          >
            {busy ? <Loader className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
            Clock-Out Sekarang
          </button>
        )}
        {clockStatus?.clocked_in && clockStatus?.clocked_out && (
          <div className="text-center py-3 rounded-full font-semibold flex items-center justify-center gap-2"
            style={{ background: 'var(--portal-success-bg)', color: 'var(--portal-success)' }}>
            <CheckCircle className="w-4 h-4" /> Kehadiran hari ini telah lengkap
          </div>
        )}

        {/* Office GPS hint strip */}
        {officeConfig && !clockStatus?.clocked_in && (
          <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: 'var(--portal-text-secondary)' }}>
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">
              Pejabat: <strong style={{ color: 'var(--portal-text)' }}>{officeConfig.address || `${officeConfig.lat}, ${officeConfig.lng}`}</strong>
              {' · '}radius {officeConfig.radius_meters}m
            </span>
          </div>
        )}

        {error && (
          <div className="mt-3 text-xs p-2.5 rounded-lg flex items-start gap-2"
            style={{ background: 'var(--portal-danger-bg)', color: 'var(--portal-danger)' }}>
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
      </div>

      {/* BALANCE CARDS — horizontal scroll mobile / grid desktop */}
      <div data-testid="balance-cards" className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto md:overflow-visible no-scrollbar snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0">
        <BalanceCard
          testId="bc-leave"
          icon={BookOpen}
          label="Baki Cuti Tahunan"
          value={leaveBalance != null ? `${leaveBalance} hari` : 'Tiada rekod'}
          hint={leaveBalance != null ? 'Klik untuk mohon cuti' : null}
          onClick={() => onJump('leave')}
        />
        <BalanceCard
          testId="bc-salary"
          icon={Wallet}
          label="Slip Gaji Terkini"
          value={netSalary != null ? `RM ${Number(netSalary).toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Belum dijana'}
          hint={netSalary != null ? 'Lihat & muat turun PDF' : null}
          onClick={() => onJump('payslip')}
        />
        <BalanceCard
          testId="bc-hours"
          icon={Clock}
          label="Jam Bekerja Bulan Ini"
          value={hoursThisMonth > 0 ? `${hoursThisMonth.toFixed(1)} jam` : 'Belum dikira'}
          hint={hoursThisMonth > 0 ? 'Lihat sejarah kehadiran' : null}
          onClick={() => onJump('attendance')}
        />
      </div>

      {/* PENDING ITEMS FEED */}
      {pendingCount > 0 && (
        <div data-testid="pending-feed" className="portal-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="portal-heading text-base font-bold flex items-center gap-2" style={{ color: 'var(--portal-text)' }}>
              <ShieldCheck className="w-4 h-4" style={{ color: 'var(--portal-maroon)' }} />
              Menunggu Kelulusan
              <span className="px-2 py-0.5 rounded-full text-[11px]"
                style={{ background: 'var(--portal-maroon-muted)', color: 'var(--portal-maroon)' }}>
                {pendingCount}
              </span>
            </h3>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--portal-border)' }}>
            {pendingLeaves.slice(0, 3).map((l, i) => (
              <PendingItem key={`lv-${i}`} kind="Cuti" title={`${l.leave_type || 'Cuti'} · ${l.start_date} → ${l.end_date}`}
                days={l.days} statusColor="warning" onClick={() => onJump('leave')} />
            ))}
            {pendingOT.slice(0, 3).map((l, i) => (
              <PendingItem key={`ot-${i}`} kind="OT Claim" title={`${l.date} · ${l.hours}j × ${l.multiplier}x`}
                days={`RM ${Number(l.amount || 0).toFixed(2)}`} statusColor="warning" onClick={() => onJump('overtime')} />
            ))}
            {pendingExp.slice(0, 3).map((l, i) => (
              <PendingItem key={`ex-${i}`} kind="Tuntutan" title={`${l.merchant || l.description || 'Tuntutan'} · ${l.date || ''}`}
                days={`RM ${Number(l.amount || 0).toFixed(2)}`} statusColor="warning" onClick={() => onJump('expense')} />
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Drawer — prevents misclick */}
      <Drawer open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DrawerContent data-testid="clock-confirm-drawer" className="portal-scope">
          <div className="mx-auto w-full max-w-md px-2">
            <DrawerHeader>
              <DrawerTitle className="portal-heading text-xl">
                {confirmKind === 'in' ? 'Sahkan Clock-In?' : 'Sahkan Clock-Out?'}
              </DrawerTitle>
              <DrawerDescription>
                {confirmKind === 'in'
                  ? 'Pastikan lokasi dan masa anda betul sebelum mengesahkan.'
                  : 'Anda akan menutup rekod kehadiran untuk hari ini.'}
              </DrawerDescription>
            </DrawerHeader>

            <div className="px-4 pb-2 space-y-3">
              {/* Time preview */}
              <div className="portal-card p-4 flex items-center justify-between">
                <div>
                  <p className="text-[11px] tracking-wider uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>Masa</p>
                  <p className="portal-heading text-2xl font-bold" style={{ color: 'var(--portal-text)' }}>
                    {new Date().toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </p>
                </div>
                <Clock className="w-8 h-8" style={{ color: 'var(--portal-maroon)' }} />
              </div>

              {/* Location preview */}
              <div className="portal-card p-4">
                <p className="text-[11px] tracking-wider uppercase font-semibold mb-2" style={{ color: 'var(--portal-text-secondary)' }}>Lokasi</p>
                {!locCheck && !error && (
                  <div data-testid="drawer-loc-loading" className="flex items-center gap-3">
                    <Loader className="w-5 h-5 animate-spin" style={{ color: 'var(--portal-maroon)' }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--portal-text)' }}>Mengesahkan lokasi...</p>
                      <p className="text-xs" style={{ color: 'var(--portal-text-secondary)' }}>Benarkan akses GPS pada browser anda</p>
                    </div>
                  </div>
                )}
                {error && (
                  <div data-testid="drawer-loc-error" className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--portal-danger-bg)' }}>
                      <AlertCircle className="w-5 h-5" style={{ color: 'var(--portal-danger)' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold" style={{ color: 'var(--portal-danger)' }}>
                        GPS tidak dapat diakses
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--portal-text-secondary)' }}>
                        {error}
                      </p>
                    </div>
                  </div>
                )}
                {locCheck && !error && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: locCheck?.ok ? 'var(--portal-success-bg)' : 'var(--portal-danger-bg)' }}>
                      <MapPin className="w-5 h-5" style={{ color: locCheck?.ok ? 'var(--portal-success)' : 'var(--portal-danger)' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--portal-text)' }}>
                        {locCheck?.ok ? 'Dalam kawasan pejabat ✓' : 'Di luar kawasan pejabat'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--portal-text-secondary)' }}>
                        Jarak: {locCheck.distance}m · Radius dibenarkan: {officeConfig?.radius_meters || 150}m
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {locCheck && !locCheck.ok && !error && (
                <div className="text-xs p-2.5 rounded-lg flex items-start gap-2"
                  style={{ background: 'var(--portal-warning-bg)', color: '#8B5A00' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Anda tidak berada dalam kawasan pejabat. Clock-in di lokasi ini mungkin ditolak atau dibendera.
                </div>
              )}

              {/* Optional selfie (only on clock-IN) — opt-in, privacy aware */}
              {confirmKind === 'in' && (
                <SelfieCapture
                  selfieB64={selfieB64}
                  onCapture={setSelfieB64}
                  onClear={() => setSelfieB64(null)}
                />
              )}
            </div>

            <DrawerFooter>
              <button
                data-testid="confirm-clock-btn"
                onClick={confirmClock}
                disabled={busy || !position}
                className="w-full py-3.5 rounded-full font-bold text-base portal-btn flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'var(--portal-maroon)', color: '#FFF' }}
              >
                {busy ? <Loader className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                Ya, Sahkan {confirmKind === 'in' ? 'Clock-In' : 'Clock-Out'}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                className="w-full py-3 rounded-full font-semibold text-sm portal-btn"
                style={{ background: 'transparent', color: 'var(--portal-text-secondary)' }}
              >
                Batal
              </button>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const BalanceCard = ({ icon: Icon, label, value, hint, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className="portal-card portal-card-interactive p-4 sm:p-5 text-left flex-shrink-0 snap-start w-[78%] sm:w-auto"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="w-9 h-9 rounded-full flex items-center justify-center"
        style={{ background: 'var(--portal-maroon-muted)' }}>
        <Icon className="w-4 h-4" style={{ color: 'var(--portal-maroon)' }} />
      </div>
      <ArrowRight className="w-4 h-4 opacity-40" style={{ color: 'var(--portal-text)' }} />
    </div>
    <p className="text-[11px] tracking-wider uppercase font-semibold mb-1" style={{ color: 'var(--portal-text-secondary)' }}>
      {label}
    </p>
    <p className="portal-heading text-xl font-bold truncate" style={{ color: 'var(--portal-text)' }}>
      {value}
    </p>
    {hint && (
      <p className="text-xs mt-2" style={{ color: 'var(--portal-text-secondary)' }}>
        {hint}
      </p>
    )}
  </button>
);

const PendingItem = ({ kind, title, days, statusColor = 'warning', onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between py-3 text-left portal-btn hover:opacity-80"
  >
    <div className="min-w-0 flex-1">
      <p className="text-[10px] tracking-wider uppercase font-semibold" style={{ color: 'var(--portal-text-secondary)' }}>
        {kind}
      </p>
      <p className="text-sm font-semibold truncate" style={{ color: 'var(--portal-text)' }}>
        {title}
      </p>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
      {days != null && (
        <span className="text-xs font-semibold" style={{ color: 'var(--portal-text)' }}>{days}{typeof days === 'number' ? ' hari' : ''}</span>
      )}
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
        style={{
          background: statusColor === 'warning' ? 'var(--portal-warning-bg)' : 'var(--portal-success-bg)',
          color: statusColor === 'warning' ? '#B25A00' : 'var(--portal-success)',
        }}>
        Menunggu
      </span>
      <ChevronRight className="w-4 h-4 opacity-40" style={{ color: 'var(--portal-text)' }} />
    </div>
  </button>
);

// Legacy, still used by older callers. Now kept but retrofit to new palette.
const QuickStat = ({ label, value, valueColor }) => (
  <div className="portal-card p-3">
    <p className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--portal-text-secondary)' }}>{label}</p>
    <p className="text-sm font-bold mt-1 truncate" style={{ color: valueColor || 'var(--portal-text)' }}>{value}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// PWA INSTALL CARD — show install instructions context-aware (iOS vs Android)
// ─────────────────────────────────────────────────────────────────────────
const InstallAppCard = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('portal-install-dismissed') === '1'; } catch { return false; }
  });

  const isIOS = useMemo(
    () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window),
    []
  );
  const isStandalone = useMemo(
    () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone,
    []
  );

  useEffect(() => {
    if (isStandalone) { setInstalled(true); return; }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isStandalone]);

  // Hide entirely when already installed, dismissed by user, or running as PWA
  if (installed || dismissed) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('Sedang dipasang...');
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem('portal-install-dismissed', '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div data-testid="install-app-card" className="portal-card p-4 relative">
      <button
        data-testid="install-dismiss-btn"
        onClick={handleDismiss}
        aria-label="Tutup"
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center portal-btn"
        style={{ color: 'var(--portal-text-secondary)' }}
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--portal-maroon-muted)' }}>
          <Smartphone className="w-5 h-5" style={{ color: 'var(--portal-maroon)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="portal-heading text-sm font-bold" style={{ color: 'var(--portal-text)' }}>
            Pasang sebagai App
          </h4>
          <p className="text-[11px] sm:text-xs mt-0.5 leading-snug" style={{ color: 'var(--portal-text-secondary)' }}>
            Akses pantas dari skrin utama phone · clock-in lebih cepat.
          </p>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {!isIOS && deferredPrompt && (
              <button
                data-testid="install-app-btn"
                onClick={handleInstall}
                className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 portal-btn"
                style={{ background: 'var(--portal-maroon)', color: '#FFF' }}
              >
                <Download className="w-3.5 h-3.5" /> Install
              </button>
            )}
            {isIOS && (
              <button
                data-testid="install-ios-help-btn"
                onClick={() => setShowIOSHelp(!showIOSHelp)}
                className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 portal-btn"
                style={{ background: 'var(--portal-maroon)', color: '#FFF' }}
              >
                <Smartphone className="w-3.5 h-3.5" /> Cara Pasang di iPhone
              </button>
            )}
            {!isIOS && !deferredPrompt && (
              <p className="text-[11px] italic" style={{ color: 'var(--portal-text-secondary)' }}>
                Tip: Buka di Chrome/Edge → Menu (⋮) → "Install app"
              </p>
            )}
          </div>
          {isIOS && showIOSHelp && (
            <div className="mt-3 portal-card p-3 text-[11px] space-y-1.5" style={{ color: 'var(--portal-text)' }}>
              <p className="font-bold" style={{ color: 'var(--portal-maroon)' }}>iPhone/iPad Safari:</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Tap butang <strong>Share</strong> di bawah Safari</li>
                <li>Scroll ke bawah, tap <strong>"Add to Home Screen"</strong></li>
                <li>Tap <strong>"Add"</strong> di kanan atas</li>
                <li>Icon Portal Saya akan muncul di skrin utama</li>
              </ol>
              <p className="text-[10px] italic mt-1.5" style={{ color: 'var(--portal-text-secondary)' }}>
                Nota: Hanya berfungsi di Safari, bukan Chrome iOS.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// PROFILE Section — view & edit limited fields
// ─────────────────────────────────────────────────────────────────────────
const ProfileSection = ({ profile, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    phone: profile.phone || '',
    address: profile.address || '',
    emergency_contact_name: profile.emergency_contact_name || '',
    emergency_contact_phone: profile.emergency_contact_phone || '',
  });

  const save = async () => {
    try {
      await axios.put(`${API}/me/profile`, form);
      toast.success('Profil dikemas kini');
      setEditing(false);
      await onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal simpan profil');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-800">Maklumat Peribadi</h3>
        {!editing && (
          <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 font-medium">
            Edit
          </button>
        )}
      </div>

      <ReadField label="Nama" value={profile.name} />
      <ReadField label="IC No." value={profile.ic_number || '—'} />
      <ReadField label="Email" value={profile.email || '—'} />
      <ReadField label="Jawatan" value={profile.position || '—'} />
      <ReadField label="Jabatan" value={profile.department || '—'} />
      <ReadField label="Bank" value={profile.bank_name ? `${profile.bank_name} • ${profile.bank_account || ''}` : '—'} />

      <div className="border-t pt-3 mt-3">
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Boleh Dikemaskini Sendiri</h4>
        {editing ? (
          <div className="space-y-3">
            <EditField label="No. Telefon" value={form.phone} onChange={(v) => setForm(s => ({ ...s, phone: v }))} />
            <EditField label="Alamat" value={form.address} onChange={(v) => setForm(s => ({ ...s, address: v }))} multiline />
            <EditField label="Hubungi Kecemasan (Nama)" value={form.emergency_contact_name} onChange={(v) => setForm(s => ({ ...s, emergency_contact_name: v }))} />
            <EditField label="Hubungi Kecemasan (No.)" value={form.emergency_contact_phone} onChange={(v) => setForm(s => ({ ...s, emergency_contact_phone: v }))} />
            <div className="flex gap-2">
              <button data-testid="save-profile-btn" onClick={save} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">Simpan</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200">Batal</button>
            </div>
          </div>
        ) : (
          <>
            <ReadField label="No. Telefon" value={profile.phone || '—'} />
            <ReadField label="Alamat" value={profile.address || '—'} />
            <ReadField label="Hubungi Kecemasan" value={profile.emergency_contact_name ? `${profile.emergency_contact_name} • ${profile.emergency_contact_phone || ''}` : '—'} />
          </>
        )}
      </div>
    </div>
  );
};

const ReadField = ({ label, value }) => (
  <div className="flex justify-between items-start gap-3 py-1">
    <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-800 font-medium text-right break-words min-w-0">{value}</span>
  </div>
);

const EditField = ({ label, value, onChange, multiline = false }) => (
  <div>
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
    ) : (
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// PAYSLIP Section
// ─────────────────────────────────────────────────────────────────────────
const PayslipSection = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSlip = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/payslip?month=${month}`);
      setPayslip(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat slip gaji');
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchSlip(); }, [fetchSlip]);

  const fmt = (v) => `RM ${Number(v || 0).toFixed(2)}`;

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/me/payslip/pdf?month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Slip gaji PDF dimuat turun');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal muat turun PDF');
    }
  };

  // Support BOTH old flat shape AND new nested shape
  const earnings = payslip?.earnings || payslip || {};
  const stat = payslip?.statutory_deductions || payslip || {};
  const addl = payslip?.additional_deductions || {};
  const totals = payslip?.totals || payslip || {};

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base font-bold text-gray-800">Slip Gaji</h3>
        <div className="flex gap-2">
          <input data-testid="payslip-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none" />
          <button
            onClick={downloadPdf}
            data-testid="payslip-pdf-btn"
            disabled={!payslip || loading}
            className="flex items-center gap-1 px-3 py-1.5 bg-red-700 text-white rounded-lg text-sm hover:bg-red-800 disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {loading && <Loader className="w-6 h-6 animate-spin text-indigo-600 mx-auto py-4" />}
      {!loading && payslip && (
        <div className="space-y-2.5 text-sm">
          <PayRow label="Gaji Asas" value={fmt(earnings.basic_salary)} />
          {(earnings.allowance > 0) && <PayRow label="Allowance" value={fmt(earnings.allowance)} />}
          {(earnings.overtime_pay > 0) && (
            <PayRow label="Overtime" value={fmt(earnings.overtime_pay)}
              sub={`${earnings.overtime_hours || 0}h, ${earnings.overtime_claims_count || 0} claim`} />
          )}
          <PayRow label="Pendapatan Kasar" value={fmt(earnings.gross_salary)} bold />
          <div className="pt-2 border-t border-dashed">
            <p className="text-[11px] text-gray-400 uppercase font-semibold mb-1.5">Potongan Statutori</p>
            <PayRow label="EPF (Pekerja)" value={`-${fmt(stat.epf_employee)}`} small />
            <PayRow label="SOCSO (Pekerja)" value={`-${fmt(stat.socso_employee)}`} small />
            <PayRow label="EIS (Pekerja)" value={`-${fmt(stat.eis_employee)}`} small />
            {(stat.pcb_amount > 0) && <PayRow label="PCB (Cukai)" value={`-${fmt(stat.pcb_amount)}`} small />}
          </div>
          {(addl.lateness_deduction > 0 || addl.salary_advance > 0) && (
            <div className="pt-2 border-t border-dashed">
              <p className="text-[11px] text-gray-400 uppercase font-semibold mb-1.5">Potongan Lain</p>
              {addl.lateness_deduction > 0 && (
                <PayRow label={`Lateness (${addl.lateness_minutes_billable || 0} min)`}
                  value={`-${fmt(addl.lateness_deduction)}`} small />
              )}
              {addl.salary_advance > 0 && <PayRow label="Advance Gaji" value={`-${fmt(addl.salary_advance)}`} small />}
            </div>
          )}
          <PayRow label="Total Potongan" value={`-${fmt(totals.total_deductions)}`} small bold />
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mt-2">
            <PayRow label="Gaji Bersih (Net Pay)" value={fmt(totals.net_pay)} bold large />
          </div>
        </div>
      )}
    </div>
  );
};

const PayRow = ({ label, value, sub, bold, small, large }) => (
  <div className="flex justify-between items-center">
    <div>
      <span className={`${small ? 'text-xs' : large ? 'text-sm' : 'text-sm'} ${bold ? 'font-bold' : 'font-medium'} text-gray-700`}>{label}</span>
      {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
    </div>
    <span className={`${small ? 'text-xs' : large ? 'text-lg' : 'text-sm'} ${bold ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>{value}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// ATTENDANCE Section
// ─────────────────────────────────────────────────────────────────────────
const AttendanceSection = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/attendance?month=${month}`);
        setData(data);
      } catch (e) {
        toast.error('Gagal memuat data kehadiran');
      }
    })();
  }, [month]);

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base font-bold text-gray-800">Kehadiran Saya</h3>
        <input data-testid="att-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none" />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Stat label="Total Hari" value={data.summary.total_days} color="indigo" />
            <Stat label="Tepat Masa" value={data.summary.on_time} color="emerald" />
            <Stat label="Lewat" value={data.summary.late} color="amber" />
          </div>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Sejarah</h4>
          {data.pdf_records.length === 0 && data.clock_records.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">Tiada rekod kehadiran untuk bulan ini.</p>
          )}
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {[...data.clock_records, ...data.pdf_records].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((r, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg text-xs">
                <div>
                  <p className="font-semibold text-gray-700">{r.date}</p>
                  <p className="text-gray-500">
                    {r.clock_in_time ? `In: ${new Date(r.clock_in_time).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', hour12: false })}` : (r.check_in || '—')}
                    {' • '}
                    {r.clock_out_time ? `Out: ${new Date(r.clock_out_time).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', hour12: false })}` : (r.check_out || '—')}
                  </p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {r.status === 'late' ? 'Lewat' : (r.status || 'On time')}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className={`p-3 rounded-xl text-center border ${
    color === 'indigo' ? 'bg-indigo-50 border-indigo-200' :
    color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
    'bg-amber-50 border-amber-200'
  }`}>
    <p className={`text-2xl font-bold ${
      color === 'indigo' ? 'text-indigo-700' :
      color === 'emerald' ? 'text-emerald-700' :
      'text-amber-700'
    }`}>{value}</p>
    <p className="text-[10px] text-gray-500 uppercase font-semibold">{label}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// LEAVE Section — apply + view balance
// ─────────────────────────────────────────────────────────────────────────
const LeaveSection = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchLeave = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/leave`);
      setData(data);
    } catch (e) {
      toast.error('Gagal memuat data cuti');
    }
  }, []);
  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-800">Baki Cuti</h3>
          <button data-testid="apply-leave-btn" onClick={() => setShowForm(true)}
            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Mohon
          </button>
        </div>
        {data && (
          <div className="grid grid-cols-3 gap-2">
            <LeaveBalanceCard label="Annual" balance={data.balance.annual} taken={data.balance.annual_taken} color="indigo" />
            <LeaveBalanceCard label="MC" balance={data.balance.mc} taken={data.balance.mc_taken} color="emerald" />
            <LeaveBalanceCard label="Emergency" balance={data.balance.emergency} taken={0} color="amber" />
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <h3 className="text-base font-bold text-gray-800 mb-3">Sejarah Permohonan</h3>
        {data?.leaves?.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Tiada permohonan cuti.</p>}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {data?.leaves?.map((l) => (
            <div key={l.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-800 capitalize">{l.leave_type}</p>
                <p className="text-xs text-gray-500">{l.start_date} → {l.end_date} ({l.days} hari)</p>
                {l.reason && <p className="text-[11px] text-gray-400 mt-1 truncate">{l.reason}</p>}
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full flex-shrink-0 ${
                l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                l.status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {l.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {showForm && <ApplyLeaveForm onClose={() => setShowForm(false)} onSuccess={() => { setShowForm(false); fetchLeave(); }} />}
    </div>
  );
};

const LeaveBalanceCard = ({ label, balance, taken, color }) => (
  <div className={`p-3 rounded-xl border text-center ${
    color === 'indigo' ? 'bg-indigo-50 border-indigo-200' :
    color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
    'bg-amber-50 border-amber-200'
  }`}>
    <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
    <p className={`text-2xl font-bold ${
      color === 'indigo' ? 'text-indigo-700' :
      color === 'emerald' ? 'text-emerald-700' : 'text-amber-700'
    }`}>{balance - taken}</p>
    <p className="text-[10px] text-gray-400">/ {balance} hari</p>
  </div>
);

const ApplyLeaveForm = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    leave_type: 'annual',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [workingDays, setWorkingDays] = useState(null); // { working_days, total_days, breakdown }
  const [calcError, setCalcError] = useState('');
  const [attachmentB64, setAttachmentB64] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');

  // Auto-recompute working days when dates change
  useEffect(() => {
    let canceled = false;
    (async () => {
      setCalcError('');
      try {
        const { data } = await axios.get(`${API}/me/leave/working-days`, {
          params: { start_date: form.start_date, end_date: form.end_date },
        });
        if (!canceled) setWorkingDays(data);
      } catch (e) {
        if (!canceled) {
          setWorkingDays(null);
          setCalcError(e.response?.data?.detail || 'Tarikh tidak sah');
        }
      }
    })();
    return () => { canceled = true; };
  }, [form.start_date, form.end_date]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { setAttachmentB64(null); setAttachmentName(''); return; }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Saiz fail maksimum 3 MB');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setAttachmentB64(reader.result); setAttachmentName(file.name); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!workingDays || workingDays.working_days === 0) {
      toast.error('Tarikh yang dipilih tidak mengandungi hari bekerja.');
      return;
    }
    if (form.leave_type === 'mc' && !attachmentB64) {
      const ok = window.confirm('MC biasanya perlu sijil. Teruskan tanpa upload sijil?');
      if (!ok) return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/me/leave`, {
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
        attachment_url: attachmentB64,
      });
      haptic(15);
      toast.success('Permohonan cuti dihantar! Menunggu approval HR.');
      onSuccess();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Gagal hantar permohonan');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-xl space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Mohon Cuti</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs text-gray-500">Jenis Cuti</label>
          <select data-testid="leave-type" value={form.leave_type} onChange={(e) => setForm(s => ({ ...s, leave_type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="annual">Annual Leave</option>
            <option value="mc">Medical Certificate (MC)</option>
            <option value="emergency">Emergency</option>
            <option value="unpaid">Unpaid Leave</option>
            <option value="other">Lain-lain</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Tarikh Mula</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm(s => ({ ...s, start_date: e.target.value }))} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Tarikh Tamat</label>
            <input type="date" value={form.end_date} onChange={(e) => setForm(s => ({ ...s, end_date: e.target.value }))} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
        </div>

        {/* Working days auto-calc preview */}
        <div data-testid="working-days-preview" className="rounded-lg p-3 bg-indigo-50 border border-indigo-100">
          {calcError ? (
            <p className="text-xs text-red-600"><AlertCircle className="inline w-3.5 h-3.5 mr-1" />{calcError}</p>
          ) : workingDays ? (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-600">Hari Bekerja (auto, kecualikan hujung minggu + cuti umum)</span>
                <span className="text-xl font-bold text-indigo-700">{workingDays.working_days}</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                {workingDays.total_days} hari keseluruhan · {workingDays.total_days - workingDays.working_days} hari bukan bekerja
              </p>
              {workingDays.breakdown?.some(b => b.tag === 'public_holiday') && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Termasuk cuti umum: {workingDays.breakdown.filter(b => b.tag === 'public_holiday').map(b => b.date).join(', ')}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400">Mengira...</p>
          )}
        </div>

        <div>
          <label className="text-xs text-gray-500">Sebab</label>
          <textarea value={form.reason} onChange={(e) => setForm(s => ({ ...s, reason: e.target.value }))} rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Optional" />
        </div>

        {/* MC certificate upload (any leave type, encouraged for MC) */}
        <div>
          <label className="text-xs text-gray-500 flex items-center gap-1">
            Muat Naik Dokumen {form.leave_type === 'mc' && <span className="text-red-600">(disyorkan untuk MC)</span>}
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            data-testid="leave-attachment-input"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm file:mr-3 file:px-3 file:py-1 file:rounded-full file:border-0 file:bg-indigo-600 file:text-white file:text-xs"
          />
          {attachmentName && (
            <p className="text-[11px] text-emerald-700 mt-1 truncate">✓ {attachmentName}</p>
          )}
        </div>

        <button type="submit" disabled={busy || !workingDays?.working_days} data-testid="submit-leave-btn"
          className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50">
          {busy ? 'Menghantar…' : 'Hantar Permohonan'}
        </button>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// KPI Section
// ─────────────────────────────────────────────────────────────────────────
const KPISection = () => {
  const [kpis, setKpis] = useState([]);
  const [perf, setPerf] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [k, p] = await Promise.all([axios.get(`${API}/me/kpi`), axios.get(`${API}/me/performance`)]);
        setKpis(k.data.kpi_records || []);
        setPerf(p.data.performance_reviews || []);
      } catch (e) { toast.error('Gagal memuat KPI'); }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <h3 className="text-base font-bold text-gray-800 mb-3">KPI Saya</h3>
        {kpis.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Tiada KPI direkod.</p>}
        <div className="space-y-2">
          {kpis.map((k, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{k.kpi_title || k.title || 'KPI'}</p>
                  <p className="text-xs text-gray-500">{k.period || k.month || '—'}</p>
                </div>
                {k.score !== undefined && (
                  <span className="text-lg font-bold text-indigo-600 flex-shrink-0">{k.score}%</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <h3 className="text-base font-bold text-gray-800 mb-3">Penilaian Prestasi</h3>
        {perf.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Tiada penilaian direkod.</p>}
        <div className="space-y-2">
          {perf.map((p, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-800">Rating: {p.rating || '—'}/5</p>
              <p className="text-xs text-gray-500 mt-1">{p.review_date || '—'} • {p.reviewer || '—'}</p>
              {p.comments && <p className="text-xs text-gray-600 mt-1.5 italic">{p.comments}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// EXPENSE Section
// ─────────────────────────────────────────────────────────────────────────
const ExpenseSection = () => {
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const fetchClaims = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/expense-claims`);
      setClaims(data.claims || []);
    } catch (e) { toast.error('Gagal memuat tuntutan'); }
  }, []);
  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-gray-800">Tuntutan Saya</h3>
        <button data-testid="add-claim-btn" onClick={() => setShowForm(true)}
          className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Tambah
        </button>
      </div>
      {claims.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Tiada tuntutan.</p>}
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {claims.map((c) => (
          <div key={c.id} className="flex items-start justify-between gap-2 p-3 bg-gray-50 rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800">RM {c.amount.toFixed(2)} <span className="text-xs text-gray-500">• {c.category}</span></p>
              <p className="text-xs text-gray-500">{c.date}</p>
              {c.description && <p className="text-[11px] text-gray-600 mt-1 truncate">{c.description}</p>}
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full flex-shrink-0 ${
              c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
              c.status === 'rejected' ? 'bg-red-100 text-red-700' :
              c.status === 'paid' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`}>{c.status}</span>
          </div>
        ))}
      </div>

      {showForm && <ExpenseClaimForm onClose={() => setShowForm(false)} onSuccess={() => { setShowForm(false); fetchClaims(); }} />}
    </div>
  );
};

const ExpenseClaimForm = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), category: 'travel', amount: '', description: '',
  });
  const [busy, setBusy] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Saiz fail melebihi 8MB');
      return;
    }
    setReceiptPreview(URL.createObjectURL(file));
    setOcrLoading(true);
    setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/me/expense-claim/ocr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setOcrResult(data);
      // Auto-fill form fields from AI extraction
      setForm((s) => ({
        ...s,
        amount: data.amount > 0 ? String(data.amount) : s.amount,
        date: data.date || s.date,
        category: data.category_guess || s.category,
        description: data.merchant
          ? `${data.merchant}${s.description ? ' • ' + s.description : ''}`
          : s.description,
      }));
      if (data.confidence < 0.5) {
        toast.warning(`AI confidence rendah (${Math.round(data.confidence * 100)}%) — sila semak semula`);
      } else {
        toast.success(`Resit dibaca! Amount: RM${data.amount.toFixed(2)}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OCR gagal — sila isi manual');
    } finally {
      setOcrLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/me/expense-claim`, { ...form, amount: parseFloat(form.amount) });
      toast.success('Tuntutan dihantar! Menunggu approval.');
      onSuccess();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Gagal hantar tuntutan');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-xl space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Tuntutan Belanja</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {/* AI Receipt Scanner */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <p className="text-xs font-semibold text-purple-700">AI Receipt Scanner (Gemini)</p>
          </div>
          <p className="text-[11px] text-gray-600 mb-2">Upload gambar resit — AI akan auto-isi amaun, tarikh, dan merchant.</p>
          {receiptPreview && (
            <div className="mb-2 relative">
              <img src={receiptPreview} alt="Receipt preview" className="w-full max-h-40 object-contain rounded-lg border border-gray-200" />
              {ocrResult && (
                <div className="mt-1 flex items-center gap-2 text-[10px]">
                  <span className={`px-2 py-0.5 rounded-full font-bold ${ocrResult.confidence >= 0.7 ? 'bg-emerald-100 text-emerald-700' : ocrResult.confidence >= 0.4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                    Confidence: {Math.round(ocrResult.confidence * 100)}%
                  </span>
                  {ocrResult.merchant && <span className="text-gray-600 truncate">{ocrResult.merchant}</span>}
                </div>
              )}
            </div>
          )}
          <label
            data-testid="receipt-upload-label"
            className={`flex items-center justify-center gap-2 w-full px-3 py-2 bg-white border-2 border-dashed border-purple-300 text-purple-700 rounded-lg text-sm font-medium cursor-pointer hover:bg-purple-100 transition-colors ${ocrLoading ? 'opacity-60 pointer-events-none' : ''}`}
          >
            {ocrLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Membaca resit…</span>
              </>
            ) : (
              <>
                <Camera className="w-4 h-4" />
                <span>{receiptPreview ? 'Tukar Resit' : 'Pilih / Snap Resit'}</span>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              capture="environment"
              onChange={handleReceiptUpload}
              className="hidden"
              data-testid="receipt-upload-input"
              disabled={ocrLoading}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Tarikh</label>
            <input type="date" value={form.date} onChange={(e) => setForm(s => ({ ...s, date: e.target.value }))} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Kategori</label>
            <select data-testid="claim-category" value={form.category} onChange={(e) => setForm(s => ({ ...s, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="travel">Travel</option>
              <option value="meals">Makan</option>
              <option value="office_supplies">Office Supplies</option>
              <option value="mileage">Mileage</option>
              <option value="other">Lain-lain</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Amaun (RM)</label>
          <input data-testid="claim-amount" type="number" step="0.01" min="0.01" value={form.amount}
            onChange={(e) => setForm(s => ({ ...s, amount: e.target.value }))} required
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="0.00" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Keterangan</label>
          <textarea value={form.description} onChange={(e) => setForm(s => ({ ...s, description: e.target.value }))} rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Cth: Petrol untuk meeting client" />
        </div>
        <button type="submit" disabled={busy} data-testid="submit-claim-btn"
          className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50">
          {busy ? 'Menghantar…' : 'Hantar Tuntutan'}
        </button>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// EA FORM Section
// ─────────────────────────────────────────────────────────────────────────
const AdvanceSection = () => {
  const [data, setData] = useState({ requests: [], current_limit: { net_pay: 0, max_advance: 0 } });
  const [showForm, setShowForm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/salary-advance`);
      setData(data);
    } catch { toast.error('Gagal memuat advance history'); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const STATUS_COLOR = {
    pending:  'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    paid:     'bg-blue-100 text-blue-700',
  };

  const limit = data.current_limit || {};
  const hasActive = data.requests?.some(r => ['pending', 'approved'].includes(r.status));

  return (
    <div className="space-y-3">
      {/* Limit info */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-2xl p-4">
        <p className="text-xs text-indigo-200 uppercase tracking-wider">Had Advance Gaji</p>
        <p className="text-3xl font-black mt-1">RM{(limit.max_advance || 0).toFixed(2)}</p>
        <p className="text-xs text-indigo-100 mt-1">
          Maksimum 50% dari gaji bersih (RM{(limit.net_pay || 0).toFixed(2)})
        </p>
      </div>

      {/* Action */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-800">Permohonan Saya</h3>
        <button data-testid="add-advance-btn"
          disabled={hasActive}
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Mohon
        </button>
      </div>
      {hasActive && (
        <p className="text-xs text-amber-600 -mt-1">
          ⚠️ Anda dah ada permohonan aktif. Tunggu selesai dahulu.
        </p>
      )}

      {/* History */}
      {data.requests?.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-6">Tiada permohonan lagi.</p>
      )}
      {data.requests?.map((r) => (
        <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`my-adv-${r.id}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-lg font-bold text-gray-800">RM{r.amount_requested.toFixed(2)}</p>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>
              {r.status}
            </span>
          </div>
          <p className="text-xs text-gray-500">{(r.submitted_at || '').slice(0, 10)}</p>
          <p className="text-xs text-gray-700 mt-1">{r.reason}</p>
          {r.rejected_reason && <p className="text-xs text-red-600 mt-1">❌ {r.rejected_reason}</p>}
        </div>
      ))}

      {showForm && (
        <AdvanceRequestForm
          maxAmount={limit.max_advance || 0}
          onClose={() => setShowForm(false)}
          onCreated={fetchData}
        />
      )}
    </div>
  );
};

const AdvanceRequestForm = ({ maxAmount, onClose, onCreated }) => {
  const [form, setForm] = useState({ amount: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/me/salary-advance`, {
        amount: parseFloat(form.amount),
        reason: form.reason,
      });
      toast.success('Permohonan dihantar! Menunggu approval HR.');
      onCreated();
      onClose();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Gagal hantar');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Mohon Advance Gaji</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
          Maksimum dibenarkan: <span className="font-bold">RM{maxAmount.toFixed(2)}</span>
        </div>
        <div>
          <label className="text-xs text-gray-500">Amaun (RM)</label>
          <input type="number" step="0.01" min="0.01" max={maxAmount} required
            data-testid="adv-amount" value={form.amount}
            onChange={(e) => setForm(s => ({ ...s, amount: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="0.00" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Sebab Permohonan</label>
          <textarea required data-testid="adv-reason" value={form.reason}
            onChange={(e) => setForm(s => ({ ...s, reason: e.target.value }))}
            rows={3} maxLength={500}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            placeholder="Cth: Hospital bill family member" />
        </div>
        <button type="submit" disabled={busy} data-testid="submit-adv-btn"
          className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50">
          {busy ? 'Menghantar…' : 'Hantar Permohonan'}
        </button>
      </form>
    </div>
  );
};

const EAFormSection = () => {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/ea-form?year=${year}`);
        setData(data);
      } catch (e) { toast.error('Gagal generate EA Form'); }
    })();
  }, [year]);

  const fmt = (v) => `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base font-bold text-gray-800">EA Form (Anggaran)</h3>
        <select data-testid="ea-year" value={year} onChange={(e) => setYear(parseInt(e.target.value))}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm">
          {[0, 1, 2, 3].map((i) => {
            const y = new Date().getFullYear() - i - 1;
            return <option key={y} value={y}>Tahun {y}</option>;
          })}
        </select>
      </div>
      {data && (
        <>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4">
            <Building2 className="w-4 h-4 text-indigo-600 mb-1" />
            <p className="text-xs text-indigo-900">
              <strong>{data.staff.name}</strong> • {data.staff.ic_number || '—'}<br />
              {data.staff.position || '—'} • {data.staff.department || '—'}
            </p>
          </div>
          <div className="space-y-2">
            <PayRow label="Pendapatan Kasar Tahunan" value={fmt(data.annual_summary.gross_income)} bold />
            <div className="pt-2 border-t border-dashed">
              <p className="text-[11px] text-gray-400 uppercase font-semibold mb-1.5">Potongan Tahunan</p>
              <PayRow label="EPF (Pekerja)" value={fmt(data.annual_summary.epf_employee)} small />
              <PayRow label="SOCSO (Pekerja)" value={fmt(data.annual_summary.socso_employee)} small />
              <PayRow label="EIS (Pekerja)" value={fmt(data.annual_summary.eis_employee)} small />
              <PayRow label="PCB Total" value={fmt(data.annual_summary.pcb_total)} small />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mt-2">
              <PayRow label="Take-home Tahunan" value={fmt(data.annual_summary.net_take_home)} bold large />
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-4 italic">{data.note}</p>
        </>
      )}
    </div>
  );
};

// ============================================================
// OVERTIME CLAIM SECTION
// ============================================================
const OvertimeSection = () => {
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 1,
    ot_type: 'normal',
    reason: '',
    project: '',
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/me/overtime-claims`);
      setClaims(data.claims || []);
      setSummary(data.summary || {});
    } catch (e) { /* silent */ }
  };

  const fetchPreview = async () => {
    if (!form.hours || form.hours <= 0) return;
    try {
      const { data } = await axios.get(`${API}/overtime-claims/rate-preview`, {
        params: { hours: form.hours, ot_type: form.ot_type },
      });
      setPreview(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (showForm) fetchPreview(); }, [form.hours, form.ot_type, showForm]);

  const submit = async () => {
    if (!form.reason || form.reason.length < 3) {
      toast.error('Sila isi sebab OT (minimum 3 huruf)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/me/overtime-claim`, form);
      toast.success('OT claim dihantar untuk approval');
      setShowForm(false);
      setForm({ ...form, reason: '', project: '' });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ralat hantar claim');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (s) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${styles[s] || 'bg-gray-100'}`}>{s.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4" data-testid="overtime-section">
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-800">OT Claims</h3>
          <button
            onClick={() => setShowForm(true)}
            data-testid="ot-new-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> Mohon OT
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-[10px] text-gray-500 uppercase">Total</p>
            <p className="text-lg font-semibold">{summary.count || 0}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-2">
            <p className="text-[10px] text-amber-700 uppercase">Pending</p>
            <p className="text-lg font-semibold text-amber-700">RM{(summary.pending_amount || 0).toFixed(2)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-2">
            <p className="text-[10px] text-emerald-700 uppercase">Approved</p>
            <p className="text-lg font-semibold text-emerald-700">RM{(summary.approved_amount || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {claims.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">Belum ada OT claim. Klik "Mohon OT" untuk hantar.</p>
        )}
        {claims.map((c) => (
          <div key={c.id} className="bg-white rounded-lg p-3 border border-gray-200" data-testid={`ot-claim-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-800">{c.date}</span>
                  {statusBadge(c.status)}
                </div>
                <p className="text-xs text-gray-600">{c.ot_label} • {c.hours}h × {c.multiplier}× = <span className="font-semibold text-gray-800">RM{c.amount.toFixed(2)}</span></p>
                <p className="text-xs text-gray-500 mt-1 truncate">{c.reason}</p>
                {c.rejected_reason && <p className="text-xs text-red-600 mt-1">Ditolak: {c.rejected_reason}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Submit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-2" data-testid="ot-form-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Mohon Overtime Claim</h3>
              <button onClick={() => setShowForm(false)} data-testid="ot-form-close">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Tarikh OT</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="ot-form-date"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Jumlah Jam</label>
                  <input
                    type="number"
                    step="0.5" min="0.5" max="24"
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })}
                    data-testid="ot-form-hours"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Jenis OT</label>
                  <select
                    value={form.ot_type}
                    onChange={(e) => setForm({ ...form, ot_type: e.target.value })}
                    data-testid="ot-form-type"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="normal">Hari Biasa (1.5×)</option>
                    <option value="rest_day">Rest Day (2.0×)</option>
                    <option value="public_holiday">Cuti Umum (3.0×)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Sebab OT *</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="ot-form-reason"
                  rows={2}
                  placeholder="Cth: Hantar deliverable kepada client"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Project (optional)</label>
                <input
                  type="text"
                  value={form.project}
                  onChange={(e) => setForm({ ...form, project: e.target.value })}
                  data-testid="ot-form-project"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              {preview && (
                <div className="bg-indigo-50 rounded-lg p-3 text-xs">
                  <p className="text-indigo-700 font-medium mb-1">Anggaran Bayaran</p>
                  <p>Hourly rate: RM{preview.hourly_rate?.toFixed(2)}/h × {form.hours}h × {preview.multiplier}×</p>
                  <p className="text-base font-bold text-indigo-700 mt-1">= RM{preview.estimated_amount_rm?.toFixed(2)}</p>
                  <p className="text-[10px] text-gray-500 mt-1">Akta Kerja 1955. Jumlah final tertakluk approval HR.</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm"
              >
                Batal
              </button>
              <button
                onClick={submit}
                disabled={saving}
                data-testid="ot-form-submit"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? 'Hantar...' : 'Hantar Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfServicePortalPage;
